"use client";

import type { BotIdTags } from "@homeinstead/analytics";
import type { FieldValues, UseFormGetValues } from "react-hook-form";
import type { LeadResponse } from "~www/types/forms/lead-response";

import { track } from "@homeinstead/analytics";
import { useEffect, useRef } from "react";

type InquirySubmitResult = "success" | "server-error" | "validation-error";

type InquirySubmissionTrackingOptions<T extends FieldValues> = {
  eventName: string;
  getProperties: (
    result: InquirySubmitResult,
    getValues: UseFormGetValues<T>,
    tags: BotIdTags,
  ) => Record<string, unknown>;
  getValues: UseFormGetValues<T>;
};

function useInquirySubmissionTracking<T extends FieldValues>(
  state: LeadResponse<unknown> | null,
  options: InquirySubmissionTrackingOptions<T>,
): void {
  const { eventName, getProperties, getValues } = options;
  const didTrackSuccess = useRef(false);
  const didTrackFailure = useRef(false);

  useEffect(() => {
    if (state == null) {
      return;
    }

    if (state.ok) {
      if (!didTrackSuccess.current) {
        didTrackSuccess.current = true;
        const properties = getProperties("success", getValues, state.tags);
        if (process.env.NODE_ENV === "development") {
          console.log("[Segment] event:", eventName, properties);
        }
        track(eventName, properties);
      }
      return;
    }

    if (!didTrackFailure.current) {
      didTrackFailure.current = true;
      const result: InquirySubmitResult = state.validationErrors
        ? "validation-error"
        : "server-error";
      const properties = getProperties(result, getValues, state.tags);
      if (process.env.NODE_ENV === "development") {
        console.log("[Segment] event:", eventName, properties);
      }
      track(eventName, properties);
    }
  }, [eventName, getProperties, getValues, state]);
}

export { useInquirySubmissionTracking };
